// Prévia textual de uma mensagem para listagens (Mensagens Pendentes,
// Relatório de Mensagens). Extraído de pages/PendingMessages para virar fonte
// única — os rótulos entram por parâmetro para que cada tela possa usar i18n
// ou strings próprias.

import { i18n } from "../translate/i18n";

const bracket = (key) => `[${i18n.t(`messagePreview.${key}`)}]`;

// Marcadores que o backend grava em português no body/lastMessage quando a
// mídia não tem legenda (wbotMessageListener: mediaTypeLastMessageLabel e
// getBodyMessage). São traduzidos na exibição; texto comum passa direto.
const MARKERS = {
  imagem: "image",
  "vídeo": "video",
  video: "video",
  "áudio": "audio",
  audio: "audio",
  arquivo: "file",
  sticker: "sticker",
  figurinha: "sticker",
  "varios contatos": "contacts",
  "vários contatos": "contacts",
  "🛍️ produto do catálogo": "catalogProduct",
  "🛒 pedido recebido": "orderReceived",
  "mensagem enviada externamente": "externalMessage",
  reaction: "reaction",
  recebido: "received",
};

export const translateMessageMarker = (text) => {
  if (text === null || text === undefined) return "";
  const raw = String(text);
  const normalized = raw.replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  if (normalized.includes("data:image/png;base64")) return i18n.t("messagePreview.location");
  const key = MARKERS[normalized.toLowerCase()];
  if (key) return i18n.t(`messagePreview.${key}`);
  const contact = normalized.match(/^Contato Enviado:\s*(.*)$/i);
  if (contact) return i18n.t("messagePreview.contactSent", { name: contact[1] });
  return raw;
};

// Mensagem interativa (botões, lista, template, enquete): o body já é o texto
// legível. Sem isto, o farejamento por string do dataJson abaixo veria o
// thumbnail/contexto embutido e prefixaria a prévia com "[Imagem]".
const hasInteractiveEnvelope = (message) => {
  const raw = message?.dataJson;
  if (!raw) return false;
  if (typeof raw === "object") return Boolean(raw.interactive);
  if (!raw.includes('"interactive"')) return false;
  try {
    return Boolean(JSON.parse(raw)?.interactive);
  } catch (_error) {
    return false;
  }
};

export const getMediaPrefix = (message) => {
  const normalized = String(message?.mediaType || "").trim().toLowerCase();
  const mediaUrl = String(message?.mediaUrl || "").trim().toLowerCase();
  const dataJsonText = String(message?.dataJson || "").toLowerCase();

  if (normalized) {
    if (["image", "imagemessage", "sticker", "stickermessage"].includes(normalized)) {
      return bracket("image");
    }

    if (["video", "videomessage"].includes(normalized)) {
      return bracket("video");
    }

    if (["audio", "audiomessage", "ptt"].includes(normalized)) {
      return bracket("audio");
    }

    if (
      ["document", "documentmessage", "application", "applicationmessage"].includes(normalized)
      || normalized.startsWith("application/")
    ) {
      return bracket("file");
    }
  }

  if (mediaUrl) {
    if (/\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(mediaUrl)) {
      return bracket("image");
    }

    if (/\.(mp4|mov|avi|mkv|webm|3gp)(\?|#|$)/i.test(mediaUrl)) {
      return bracket("video");
    }

    if (/\.(mp3|ogg|opus|wav|m4a|aac)(\?|#|$)/i.test(mediaUrl)) {
      return bracket("audio");
    }

    return bracket("file");
  }

  if (hasInteractiveEnvelope(message)) {
    return null;
  }

  if (
    dataJsonText.includes("stickermessage")
    || dataJsonText.includes("imagemessage")
    || dataJsonText.includes("image/")
  ) {
    return bracket("image");
  }

  if (dataJsonText.includes("videomessage") || dataJsonText.includes("video/")) {
    return bracket("video");
  }

  if (
    dataJsonText.includes("audiomessage")
    || dataJsonText.includes("ptt")
    || dataJsonText.includes("audio/")
  ) {
    return bracket("audio");
  }

  if (dataJsonText.includes("documentmessage") || dataJsonText.includes("application/")) {
    return bracket("file");
  }

  return null;
};

// Função (não constante de módulo) para o texto seguir o idioma em uso.
const defaultLabels = () => ({
  deleted: i18n.t("messagePreview.deleted"),
  fileTypePrefix: i18n.t("messagePreview.file"),
  withoutContent: i18n.t("messagePreview.withoutContent"),
});

export const getMessagePreview = (message, labels = {}) => {
  const { deleted, fileTypePrefix, withoutContent } = { ...defaultLabels(), ...labels };

  if (!message) return "-";
  if (message.isDeleted) return deleted;

  const pendingPreview = String(message.pendingPreview || "").trim();
  if (pendingPreview) return pendingPreview;

  const body = String(message.body || "").trim();

  const prefix = getMediaPrefix(message);
  if (prefix) {
    return body ? `${prefix} ${body}` : prefix;
  }

  if (body) return body;

  const mediaType = String(message.mediaType || "").toLowerCase();
  if (mediaType) {
    return `${fileTypePrefix} (${mediaType})`;
  }

  return withoutContent;
};

export default getMessagePreview;
